import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

import { useAppDispatch } from '@/store';
import { authenticationCheck } from '@/store/slices/authentication';
import { OWNERS_HUB_ROUTES } from '@/constants/routes';
import { Button, Text } from '@/nova/components';

import { Card, Message, Screen, Title } from './verify-email.styles';

type Phase = 'verifying' | 'success' | 'invalid';

function verifyCodeUrl(code: string): string | null {
  const userApiUrl = (window as unknown as { config?: { userApiUrl?: string } }).config?.userApiUrl;
  if (!userApiUrl) {
    return null;
  }
  return `${userApiUrl.replace(/\/+$/, '')}/profile/verify-code?code=${encodeURIComponent(code)}`;
}

/**
 * `/verify-email?code=<uuid>` — redeems the email-verification code sent after a Telegram signup
 * (backend `GET /user/profile/verify-code`, public / no auth, so it works even on a device where the
 * owner is not signed in). On success it refreshes `email_verified` (via `/auth/check`) so the
 * verification banner clears for a signed-in owner.
 */
const VerifyEmail = (): React.ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [phase, setPhase] = React.useState<Phase>('verifying');
  const ran = React.useRef<boolean>(false);

  React.useEffect(() => {
    if (ran.current) {
      return;
    }
    ran.current = true;

    const code = searchParams.get('code');
    const url = code ? verifyCodeUrl(code) : null;
    if (!url) {
      setPhase('invalid');
      return;
    }

    axios
      .get(url)
      .then(() => {
        setPhase('success');
        // Refresh email_verified for a signed-in owner so the banner clears; ignore if not logged in.
        dispatch(authenticationCheck()).catch(() => undefined);
      })
      .catch(() => setPhase('invalid'));
  }, [dispatch, searchParams]);

  if (phase === 'success') {
    return (
      <Screen>
        <Card>
          <Title>
            <Text text="Email verified" />
          </Title>
          <Message>
            <Text text="Your email address is confirmed. You can continue in the studio." />
          </Message>
          <Button
            label="Continue"
            fullWidth
            onClick={() => navigate(OWNERS_HUB_ROUTES.STUDIO_HOME, { replace: true })}
          />
        </Card>
      </Screen>
    );
  }

  if (phase === 'invalid') {
    return (
      <Screen>
        <Card>
          <Title>
            <Text text="This link is invalid or has expired" />
          </Title>
          <Message>
            <Text text="Please request a new verification email and try again." />
          </Message>
        </Card>
      </Screen>
    );
  }

  return (
    <Screen>
      <Card>
        <Message>
          <Text text="Verifying your email..." />
        </Message>
      </Card>
    </Screen>
  );
};

export default VerifyEmail;
